import { useEffect, useState } from "react";

/**
 * 관세사 신고이력 → 지도 신호(57차). 이력 패널이 검색어별 세번 분포를 게시하고, 사전 점검 카드의 지도가 구독한다.
 *
 * 신호는 "관행"이지 판정이 아니다 — 지도는 강한 신호일 때만 그 세번까지 파란 추천 길을 긋고(답 아님·open 유지),
 * 모든 후보 마디에 `이력 N%` 배지를 붙여 판단 근거를 사용자에게 준다(2026-09-16 사용자: "선은 잇되 근거 %는 주고 판단은 사용자가").
 *
 * signal = { q, top: { hsk, share, observations, matched }, byCode: { hsk: { share, observations } }, documents, partial }
 *  - share: 신고비율(0~100), observations: 관측 수(원본 행 수, 독립 신고 수 아님)
 *  - matched: 1위 세번 안에서 일치율 100%인 문서의 관측 수 — "같은 물품"이라는 근거의 양
 *  - partial: 집계 API 없이 표시된 문서(≤50)로 만든 분포
 */
export const HISTORY_SIGNAL_EVENT = "loginhs:history-signal";
export const STRONG_SHARE = 80;
export const STRONG_MATCHED = 10;

const normalizeQuery = (q) => String(q || "").trim().replace(/\s+/g, " ");
let current = null;

export function publishHistorySignal(signal) {
  const q = normalizeQuery(signal?.q);
  current = signal && q ? { ...signal, q } : null;
  try { window.dispatchEvent(new CustomEvent(HISTORY_SIGNAL_EVENT, { detail: current })); } catch (_) { /* ignore */ }
}

export function clearHistorySignal(q) {
  if (!current || current.q !== normalizeQuery(q)) return;
  current = null;
  try { window.dispatchEvent(new CustomEvent(HISTORY_SIGNAL_EVENT, { detail: null })); } catch (_) { /* ignore */ }
}

export function peekHistorySignal(q) {
  return current && current.q === normalizeQuery(q) ? current : null;
}

export function resetHistorySignal() { current = null; }

/** 카드·지도가 구독한다. 검색어가 다르면 null. */
export function useHistorySignal(q) {
  const key = normalizeQuery(q);
  const [sig, setSig] = useState(() => peekHistorySignal(key));
  useEffect(() => {
    setSig(peekHistorySignal(key));
    const on = (e) => { const s = e.detail; setSig(s && s.q === key ? s : (current && current.q === key ? current : null)); };
    window.addEventListener(HISTORY_SIGNAL_EVENT, on);
    return () => window.removeEventListener(HISTORY_SIGNAL_EVENT, on);
  }, [key]);
  return sig;
}

/** 강한 신호 — 한 세번으로 모였고(신고비율 80% 이상) 같은 물품 근거가 충분하다(일치율 100% 관측 10건 이상). */
export function strongHistory(signal) {
  const t = signal?.top;
  return Boolean(t && typeof t.share === "number" && t.share >= STRONG_SHARE && Number.isSafeInteger(t.matched) && t.matched >= STRONG_MATCHED);
}

export const LEAD_SHARE = 50;
export const LEAD_RATIO = 2;

/**
 * 59차: 추천 길을 긋는 신호 = 강한 신호, 또는 "앞선 신호"(1위가 과반이고 2위의 두 배 이상, 같은 물품 근거 10건 이상).
 * 8542.39-1000 63% vs 8542.31-1000 26% 같은 갈림은 1위까지 길을 잇되 띠에 2위를 함께 적어 판단은 사용자가 한다(2026-09-16).
 * 반환: { kind: "strong" | "lead", top, second } 또는 null(배지만).
 */
export function leadHistory(signal) {
  const t = signal?.top;
  // 2026-09-23: 원문 검색어로 0건이라 다른 말(LLM 영문 품명 등)로 다시 찾은 신호가 한 낱말이면 길을 긋지 않는다(배지만).
  // 예: "금괴" → "GOLD" 로 찾아 금 장신구(7113) 72% 가 나왔다 — 한 낱말은 '같은 물품' 근거가 되지 못한다.
  if (singleWordFallback(signal)) return null;
  if (!t || typeof t.share !== "number" || !Number.isSafeInteger(t.matched) || t.matched < STRONG_MATCHED) return null;
  if (t.share >= STRONG_SHARE) return { kind: "strong", top: t, second: signal.second || null };
  const s = signal.second && typeof signal.second.share === "number" ? signal.second : null;
  if (t.share >= LEAD_SHARE && (!s || t.share >= s.share * LEAD_RATIO)) return { kind: "lead", top: t, second: s };
  return null;
}

/** 원문과 다른 한 낱말 검색어로 다시 찾은 신호인가. */
export function singleWordFallback(signal) {
  const searched = normalizeQuery(signal?.searched);
  if (!searched || searched.toUpperCase() === normalizeQuery(signal?.q).toUpperCase()) return false;
  return searched.split(" ").filter(Boolean).length < 2;
}

/** 마디 코드(호 4자리·소호 6자리·세번 10자리)에 걸리는 이력 — 앞자리가 같은 세번의 비율·관측을 합친다. 없으면 null. */
export function historyFor(signal, code) {
  const d = String(code || "").replace(/\D/g, "");
  const by = signal?.byCode;
  if (!d || !by || typeof by !== "object") return null;
  let share = 0; let observations = 0; let hit = false;
  for (const [hsk, v] of Object.entries(by)) {
    if (!hsk.startsWith(d)) continue;
    hit = true; share += Number(v?.share) || 0; observations += Number.isSafeInteger(v?.observations) ? v.observations : 0;
  }
  return hit ? { share: Math.round(share * 10) / 10, observations } : null;
}
